import { Fragment } from 'react';
import { Link, useMatch } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';

import type { MigrationDetailResponse } from '../api/migrations';
import type { MigrationGroupDetailData } from '../api/migrationGroups';
import { TEXT } from '../theme/tokens';

// The breadcrumb row the mockups put under the top bar.
//
// TWO RULES THIS FILE EXISTS TO OBEY.
//
// 1. EVERY `useMatch` IS CALLED UNCONDITIONALLY. React Router's `useMatches`
//    would be the natural tool and is NOT available: it needs a data router
//    (`createBrowserRouter`) and the app mounts `<BrowserRouter>`. So the crumbs
//    come from a fixed list of matches, all evaluated every render, and only
//    then combined. Never `useMatch(a) || useMatch(b)` — that is FD-188, where a
//    short-circuit changed the rendered hook count between routes and React
//    unmounted the tree to a blank page on navigation.
//
// 2. NAMES COME FROM THE CACHE, NEVER FROM A FETCH. A breadcrumb that issues its
//    own query would double every page's load and would show a spinner in the
//    chrome. `queryClient.getQueryData` reads what the page below has already
//    fetched under the same key and falls back to the id — so the crumb is
//    instant, and a name that has not arrived yet degrades to something true
//    rather than to a placeholder.

interface Crumb {
  label: string;
  to?: string;
}

export default function Breadcrumb() {
  const queryClient = useQueryClient();

  // All six evaluated unconditionally — see rule 1.
  const groups = useMatch('/groups');
  const groupDetail = useMatch('/groups/:groupId/*');
  const campaigns = useMatch('/migrations');
  const campaignDetail = useMatch('/migrations/:migrationId/*');
  const devices = useMatch('/devices');
  const deviceDetail = useMatch('/devices/:serialNumber/*');
  const identities = useMatch('/identities');
  const tenants = useMatch('/tenants');
  const tenantDetail = useMatch('/tenants/:tenantId/*');
  const settings = useMatch('/settings');

  // `/groups/new` and `/migrations/new` are CREATE routes that the param match
  // also catches, so without this the breadcrumb reads "Groups › new". The
  // literal is excluded rather than the match narrowed, because narrowing means
  // a second `useMatch` and the FD-188 rule is that every hook here is
  // unconditional — filtering the RESULT keeps the hook count fixed.
  const rawGroupId = groupDetail?.params.groupId ?? null;
  const rawMigrationId = campaignDetail?.params.migrationId ?? null;
  const groupId = rawGroupId === 'new' ? null : rawGroupId;
  const migrationId = rawMigrationId === 'new' ? null : rawMigrationId;
  const serialNumber = deviceDetail?.params.serialNumber ?? null;
  const tenantId = tenantDetail?.params.tenantId ?? null;

  const groupName = groupId
    ? (queryClient.getQueryData<MigrationGroupDetailData>(['migration-group', groupId])
        ?.migrationGroup.name ?? groupId)
    : null;

  const campaignName = migrationId
    ? (queryClient.getQueryData<MigrationDetailResponse>(['migration', migrationId])?.migration
        .migrationName ?? migrationId)
    : null;

  const crumbs: Crumb[] = [];
  if (groups || groupDetail) {
    crumbs.push({ label: 'Migration' }, { label: 'Groups', to: '/groups' });
    if (groupName) crumbs.push({ label: groupName });
  } else if (campaigns || campaignDetail) {
    crumbs.push({ label: 'Migration' }, { label: 'Campaigns', to: '/migrations' });
    if (campaignName) crumbs.push({ label: campaignName });
  } else if (devices || deviceDetail) {
    crumbs.push({ label: 'Resources' }, { label: 'Devices', to: '/devices' });
    if (serialNumber) crumbs.push({ label: serialNumber });
  } else if (identities) {
    crumbs.push({ label: 'Resources' }, { label: 'Identities' });
  } else if (tenants || tenantDetail) {
    crumbs.push({ label: 'Resources' }, { label: 'Tenants', to: '/tenants' });
    if (tenantId) crumbs.push({ label: tenantId });
  } else if (settings) {
    crumbs.push({ label: 'Deployment' }, { label: 'Settings' });
  }

  // Home, and anything unrouted, gets no trail — a one-item breadcrumb is chrome
  // describing nothing.
  if (crumbs.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[12px]">
      {crumbs.map((crumb, index) => (
        <Fragment key={`${crumb.label}-${index}`}>
          {index > 0 && (
            <ChevronRight size={13} strokeWidth={1.5} color={TEXT.secondary} aria-hidden="true" />
          )}
          {crumb.to && index < crumbs.length - 1 ? (
            <Link to={crumb.to} style={{ color: TEXT.secondary }} className="hover:underline">
              {crumb.label}
            </Link>
          ) : (
            <span
              style={{
                color: index === crumbs.length - 1 ? TEXT.primary : TEXT.secondary,
                fontWeight: index === crumbs.length - 1 ? 500 : 400,
              }}
              aria-current={index === crumbs.length - 1 ? 'page' : undefined}
            >
              {crumb.label}
            </span>
          )}
        </Fragment>
      ))}
    </nav>
  );
}
